import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MobileNav from "@/components/MobileNav";
import { ArrowLeft, Send, MessageCircle, Wallet } from "lucide-react";
import { toast } from "sonner";

const ChatInbox = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [ws, setWs] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchBoard();
  }, []);

  useEffect(() => {
    if (board) {
      fetchMessages();
      connectWebSocket();
    }
    return () => {
      if (ws) ws.close();
    };
  }, [board]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchBoard = async () => {
    try {
      const response = await axios.get(`${API}/board/current`, { withCredentials: true });
      if (response.data) {
        setBoard(response.data);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!board) return;
    try {
      const response = await axios.get(
        `${API}/messages/${board.board_id}`,
        { withCredentials: true }
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    if (!board) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL
      .replace("https://", "wss://")
      .replace("http://", "ws://");
    
    const websocket = new WebSocket(`${wsUrl}/ws/chat/${board.board_id}`);
    
    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      setMessages((prev) => {
        if (prev.some((m) => m.message_id === message.message_id)) {
          return prev;
        }
        return [...prev, message];
      });
    };

    websocket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    setWs(websocket);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !board) return;

    const messageContent = newMessage.trim();
    setNewMessage("");

    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          sender_id: user?.user_id,
          sender_name: user?.name || "Customer",
          content: messageContent,
          message_type: "text"
        }));
      } else {
        await axios.post(
          `${API}/messages/${board.board_id}`,
          { content: messageContent },
          { withCredentials: true }
        );
        fetchMessages();
      }
    } catch (error) {
      toast.error("Could not send message");
      setNewMessage(messageContent);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="chat-page">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 z-40 bg-white/90 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            <Link to="/wallet" className="text-gray-500 hover:text-black">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <h1 className="font-display text-xl font-bold">Chat</h1>
              {board && (
                <p className="text-sm text-gray-600 font-mono">
                  #{board.board_id.slice(-8)}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          </div>
        ) : !board ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <MessageCircle className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="font-display text-xl font-semibold text-black mb-2">No Active Appointment</h3>
            <p className="text-gray-600 mb-6 max-w-sm">
              Check in at a venue to start chatting with your technician
            </p>
            <Link to="/wallet">
              <Button className="bg-black hover:bg-gray-900 text-white rounded-full">
                <Wallet className="w-4 h-4 mr-2" />
                Go to Wallet
              </Button>
            </Link>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto h-full flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-32">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-600">
                    Start the conversation with your technician
                  </p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMine = msg.sender_id === user?.user_id;
                  return (
                    <div
                      key={msg.message_id}
                      className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                      data-testid={`message-${msg.message_id}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-3 ${
                          isMine ? "chat-bubble-sent" : "chat-bubble-received"
                        }`}
                      >
                        {!isMine && (
                          <p className="text-xs font-medium text-gray-600 mb-1">
                            {msg.sender_name}
                          </p>
                        )}
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMine ? "text-gray-300" : "text-gray-500"}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Message Input */}
      {board && (
        <div className="fixed bottom-20 lg:bottom-6 left-0 right-0 bg-white border-t border-gray-100 p-4 z-30">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={sendMessage} className="flex gap-3">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 h-12 rounded-full border-gray-200 bg-gray-50 focus:ring-black focus:border-black"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={!newMessage.trim()}
                className="h-12 w-12 rounded-full bg-black hover:bg-gray-900 p-0"
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      )}

      <MobileNav />
    </div>
  );
};

export default ChatInbox;
